<script setup lang="ts">
import { LoginEndpoint } from '@futmanager/api-sdk'
import { useRouter } from 'vue-router'
import logo from '~/assets/images/futmanager_logo.png'
import { useAuth, useRecaptcha } from '~/composables'
import { useUserStore } from '~/store'
import { $e } from '~/utils'

const router = useRouter()
const auth = useAuth()
const userStore = useUserStore()
const recaptchaContainerEl = ref<HTMLElement | null>(null)
const { enabled: recaptchaEnabled, execute: executeRecaptcha, reset: resetRecaptcha } = useRecaptcha(recaptchaContainerEl)

const isLoading = ref(true)
const isSubmitting = ref(false)
const login = ref('')
const password = ref('')

async function fetchToken() {
  const isAuthenticated = await auth.checkToken()
  if (!isAuthenticated) {
    isLoading.value = false
    return
  }

  router.push('/dashboard')
}

async function doSubmit() {
  try {
    isSubmitting.value = true
    const captchaToken = await executeRecaptcha()
    const { data } = await LoginEndpoint.login(login.value, password.value, captchaToken)

    if (!data.token || !data.refresh_token) {
      throw new Error('Invalid login response')
    }

    userStore.setSession(data)
    router.push('/dashboard')
  }
  catch (error) {
    resetRecaptcha()
    $e(error)
    console.error(error)
  }
  finally {
    isSubmitting.value = false
  }
}

onMounted(fetchToken)
</script>

<template>
  <div class="login-page">
    <div class="login-page__panel">
      <v3-loading-state v-if="isLoading" :min-height="280" />

      <form
        v-else
        class="login-page__form"
        @submit.prevent="doSubmit"
      >
        <div class="login-page__brand">
          <b-image
            :src="logo"
            :alt="$t('common.dashboard')"
            class="login-page__logo"
          />
        </div>

        <label class="login-page__field">
          <span class="login-page__label">
            <span class="i-tabler-user-circle" aria-hidden="true" />
            {{ $t('login.login') }}
          </span>
          <b-input
            v-model="login"
            type="text"
            autocomplete="username"
            :placeholder="$t('login.login')"
            :disabled="isSubmitting"
          />
        </label>

        <label class="login-page__field">
          <span class="login-page__label">
            <span class="i-tabler-lock" aria-hidden="true" />
            {{ $t('login.password') }}
          </span>
          <b-input
            v-model="password"
            type="password"
            autocomplete="current-password"
            :placeholder="$t('login.password')"
            :disabled="isSubmitting"
          />
        </label>

        <div
          v-if="recaptchaEnabled"
          ref="recaptchaContainerEl"
          class="login-page__recaptcha"
          aria-hidden="true"
        />

        <p
          v-if="recaptchaEnabled"
          class="login-page__captcha-hint"
        >
          {{ $t('login.captchaHint') }}
        </p>

        <b-button
          type="submit"
          variant="warning"
          class="login-page__submit"
          :loading="isSubmitting"
          :disabled="isSubmitting"
          :text="$t('login.join')"
          icon="login"
        />
      </form>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '~/components/admin/admin-shell-tokens' as *;

.login-page {
  @include admin-shell-tokens;

  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px 16px;
  color: var(--admin-sh-text);
  background-color: #0f172a;
  background-image: url('/images/bg-home.jpg');
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center top;

  &__panel {
    width: 100%;
    max-width: 420px;
    padding: 28px 24px;
    border-radius: var(--admin-sh-radius);
    background: rgba(15, 23, 42, 0.88);
    border: 1px solid var(--admin-sh-border);
    box-shadow: var(--admin-sh-shadow);
    animation: login-page-enter 320ms ease both;

    @include admin-shell-reduced-motion {
      animation: none;
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  &__brand {
    display: flex;
    justify-content: center;
    margin-bottom: 8px;
  }

  &__logo {
    display: block;
    height: 48px;
    width: auto;
    object-fit: contain;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: 'MontserratSemiBold', sans-serif;
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--admin-sh-accent);
  }

  &__recaptcha {
    position: absolute;
    width: 0;
    height: 0;
    overflow: hidden;
    pointer-events: none;
  }

  &__captcha-hint {
    margin: 0;
    font-size: 11px;
    line-height: 1.4;
    color: var(--admin-sh-muted, #94a3b8);
  }

  &__submit {
    width: 100%;
    margin-top: 4px;
  }

  :deep(input.form-control) {
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      -webkit-text-fill-color: var(--admin-sh-text);
      caret-color: var(--admin-sh-text);
      transition: background-color 99999s ease-out;
      box-shadow: inset 0 0 0 1000px rgba(30, 41, 59, 0.95);
    }
  }
}

@keyframes login-page-enter {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
